import React, { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  TrendingUp,
  Star,
  BarChart,
  CheckCircle,
  Favorite,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { useThemeMode } from '../contexts/ThemeContext';
import { Hero } from '../components/ui/animated-hero';
import { Pricing2 } from '../components/blocks/pricing2';
import { Feature108 } from '../components/blocks/shadcnblocks-com-feature108';
import TestimonialsColumns1 from '../components/blocks/testimonials-columns-1';
import GoogleOneTap from '../components/GoogleOneTap';

const MotionBox = motion(Box);

// Glass morphism card component
const GlassCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isDark',
})<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  background: $isDark 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '24px',
  border: $isDark
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: $isDark
    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
    : '0 8px 32px rgba(31, 38, 135, 0.15)',
  padding: theme.spacing(4),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  willChange: 'transform',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: $isDark
      ? '0 12px 48px rgba(0, 0, 0, 0.5)'
      : '0 12px 48px rgba(31, 38, 135, 0.25)',
    border: $isDark
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.5)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: $isDark
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%)',
    borderRadius: '24px',
    pointerEvents: 'none',
  }
}));



const LandingPage: React.FC = memo(() => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';


  return (
    <>
      <Helmet>
        <title>Health-Tree - Gamified Personal Development & Longevity Tracking</title>
        <meta name="description" content="Transform your health journey with Health-Tree's gamified talent tree system. Track progress, build habits, and extend your healthspan with science-backed protocols." />
        <link rel="canonical" href="https://healthtree.app/" />
        <meta property="og:title" content="Health-Tree - Gamified Personal Development & Longevity Tracking" />
        <meta property="og:description" content="Transform your health journey with Health-Tree's gamified talent tree system. Track progress, build habits, and extend your healthspan with science-backed protocols." />
        <meta property="og:url" content="https://healthtree.app/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Health-Tree - Gamified Personal Development" />
        <meta name="twitter:description" content="Transform your health journey with our gamified talent tree system." />
      </Helmet>
      <Box 
        sx={{ 
          minHeight: '100vh', 
          position: 'relative',
        }}
    >
      {/* Hero Section */}
      <Hero />
      
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>

          {/* Trust Indicators */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            sx={{ mb: 8 }}
          >
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <GlassCard
                $isDark={isDark}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '12px',
                }}
              >
                <Star sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  4.9/5 Rating
                </Typography>
              </GlassCard>
              <GlassCard
                $isDark={isDark}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '12px',
                }}
              >
                <Favorite sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  5K+ Active Users
                </Typography>
              </GlassCard>
              <GlassCard
                $isDark={isDark}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '12px',
                }}
              >
                <CheckCircle sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Scientifically Validated
                </Typography>
              </GlassCard>
            </Box>
          </MotionBox>

          {/* Feature108 Component */}
          <Feature108 
            badge=""
            heading="How it works"
            description=""
            isDark={isDark}
            tabs={[
              {
                value: "tab-1",
                icon: <TrendingUp className="h-auto w-4 shrink-0" />,
                label: "1. Decisions",
                content: {
                  badge: "Data-driven Decisions",
                  title: "Use our health decision tree.",
                  description: "Map your health journey based on scientific evidence and the time you have available.",
                  buttonText: "Health-Tree",
                  buttonUrl: "/talent-tree",
                  imageSrc: "https://shadcnblocks.com/images/block/placeholder-dark-1.svg",
                  imageAlt: "Health tracking dashboard"
                }
              },
              {
                value: "tab-2",
                icon: <AccountTree className="h-auto w-4 shrink-0" />,
                label: "2. Goals",
                content: {
                  badge: "Goal Mastery",
                  title: "Reach your health potential.",
                  description: "Set meaningful health goals and achieve them with our structured approach that breaks down complex objectives into manageable steps.",
                  buttonText: "Set Goals",
                  buttonUrl: "/weekly-lockin",
                  imageSrc: "https://shadcnblocks.com/images/block/placeholder-dark-2.svg",
                  imageAlt: "Talent tree system"
                }
              },
              {
                value: "tab-3",
                icon: <BarChart className="h-auto w-4 shrink-0" />,
                label: "3. Analytics",
                content: {
                  badge: "Health Analytics",
                  title: "Report and analyze health data.",
                  description: "Analytics on your workouts and health goals empowers you to track progress, stay motivated, and make smarter decisions for lasting results.",
                  buttonText: "View dashboard",
                  buttonUrl: "/dashboard",
                  imageSrc: "https://shadcnblocks.com/images/block/placeholder-dark-3.svg",
                  imageAlt: "Goal achievement system"
                }
              }
            ]}
          />


          {/* Testimonials Section */}
          <TestimonialsColumns1 />

          {/* Pricing Section */}
          <Box>
            <Pricing2 />
          </Box>

        </Container>
      </Box>

      {/* Google One Tap Authentication */}
      <GoogleOneTap />
    </Box>
    </>
  );
});

LandingPage.displayName = 'LandingPage';

export default LandingPage;